import type { ModalProps, ScrollAreaProps } from '@mantine/core'
import { Modal, ScrollArea, Stack, Text } from '@mantine/core'
import type { UseFormReturnType } from '@mantine/form'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { IconHexagons } from '@tabler/icons-react'
import type { DefaultError, UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import { useMutation, useQuery } from '@tanstack/react-query'
import { dasherize } from 'inflection'
import { observer } from 'mobx-react-lite'
import type { ComponentType } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'wouter'
import { navigate } from 'wouter/use-browser-location'
import type { z } from 'zod'

import type { CollapsibleContentSectionRef } from '@/components/CollapsibleContentSection'
import { Screen } from '@/components/Screen'
import type { SectionedFormProps } from '@/components/SectionedForm'
import { SectionedForm } from '@/components/SectionedForm'
import type { RouteParams } from '@/configs/routes'
import { theme } from '@/configs/theme'
import { useGlobalState } from '@/hooks/useGlobalState'
import { type StepsWithValidationResult, useStepsWithValidation } from '@/hooks/useStepsWithValidation'
import { NotFoundScreen } from '@/screens/404Screen'
import { ActionsSection } from '@/screens/ContentModuleFormScreen/ActionsSection'
import { AddDataSection } from '@/screens/ContentModuleFormScreen/AddDataSection'
import { CustomizeDeliverySection } from '@/screens/ContentModuleFormScreen/CustomizeDeliverySection'
import {
  OptionalSettingsSection,
  type OptionalSettingsSectionRef,
} from '@/screens/ContentModuleFormScreen/OptionalSettingsSection'
import { RequiredSettingsSection } from '@/screens/ContentModuleFormScreen/RequiredSettingsSection'
import { StepsSection } from '@/screens/ContentModuleFormScreen/StepsSection'
import type {
  CreateContentModuleParams,
  CreateContentModuleResponse,
  CreateFAQsParams,
  CreateResourceParams,
  CreateResourceResponse,
  CreateStepParams,
  CreateStepResponse,
  DeleteFAQsParams,
  DeleteResourceParams,
  DeleteStepParams,
  GenerateDemographicParams,
  GenerateDemographicResponse,
  GenerateMetadataParams,
  GenerateMetadataResponse,
  GenerateObjectivesParams,
  GenerateObjectivesResponse,
  GenerateOutlineParams,
  GenerateOutlineResponse,
  GetContentModuleResponse,
  GetContentModulesResponse,
  GetContentResponse,
  GetFAQsResponse,
  GetResourcesResponse,
  GetSidekicksResponse,
  GetSponsorTopicsResponse,
  UpdateContentModuleParams,
  UpdateContentModuleResponse,
  UpdateContentTextParams,
  UpdateContentTextResponse,
  UpdateFAQsParams,
  UpdateResourceParams,
  UpdateResourceResponse,
  UpdateStepParams,
} from '@/services/api/api.types'
import * as commonClasses from '@/styles/componentCommons.css'
import type { FAQItem } from '@/types/faq'
import type {
  MetadataFormSchema,
  ModuleFormSchema,
  ResourceFormSchema,
  StepFormSchema,
} from '@/utils/contentModuleForm'
import {
  castContentModuleToZodForm,
  castResourceToZodForm,
  castStepToZodForm,
  useContentModuleForm,
  useMetadataForm,
  useResourceForm,
  useStepForm,
} from '@/utils/contentModuleForm'
import { isSponsorMskCm } from '@/utils/contentModuleTables'
import { validateFieldsList } from '@/utils/forms'
import { getRoutePath } from '@/utils/navigation'

import { TopicSettingsSection } from './TopicSettingsSection'

enum FormSectionId {
  TopicSettings = 'topicSettings',
  AddData = 'addData',
  CMSettings = 'requiredSettings',
  CustomizeDelivery = 'customizeDelivery',
  CMOptionalSettings = 'optionalSettings',
  ContentSteps = 'contentSteps',
  Actions = 'actions',
}

export type CMFormSection = {
  isNew: boolean
  taskId?: string
  stepId?: string
  resourceId?: string
  isSponsorMskCM?: boolean
  isVisibleToMsk?: boolean

  moduleForm: UseFormReturnType<z.infer<ModuleFormSchema>>
  resourceForm: UseFormReturnType<z.infer<ResourceFormSchema>>
  stepForm: UseFormReturnType<z.infer<StepFormSchema>>
  metadataForm: UseFormReturnType<MetadataFormSchema>

  // FAQ State Management
  cmFAQState: {
    approved: FAQItem[]
    pending: FAQItem[]
    showValidationErrors: boolean
    isInitialized: boolean
  }
  stepFAQState: {
    approved: FAQItem[]
    pending: FAQItem[]
    showValidationErrors: boolean
    isInitialized: boolean
  }
  faqActions: {
    setCMApprovedFAQs: (faqs: FAQItem[] | ((prev: FAQItem[]) => FAQItem[])) => void
    setCMPendingFAQs: (faqs: FAQItem[] | ((prev: FAQItem[]) => FAQItem[])) => void
    setCMShowValidationErrors: (show: boolean) => void
    setCMIsInitialized: (initialized: boolean) => void
    setStepApprovedFAQs: (faqs: FAQItem[] | ((prev: FAQItem[]) => FAQItem[])) => void
    setStepPendingFAQs: (faqs: FAQItem[] | ((prev: FAQItem[]) => FAQItem[])) => void
    setStepShowValidationErrors: (show: boolean) => void
    setStepIsInitialized: (initialized: boolean) => void
    resetStepFAQState: () => void
  }

  openModal: (content?: ModalContent) => void
  closeModal: (afterCloseFn?: () => void) => void
  scrollToSection: (section: `${FormSectionId}`, delay?: number) => void
  scrollToField?: (fieldName: string, delay?: number) => void
  setFieldRefs?: (refs: Record<string, React.RefObject<HTMLElement>>) => void
  clearValidationErrors?: () => void
  clearValidationTrigger?: number
  saveCMFAQs?: () => Promise<void>
  stepSavedTrigger?: number
  signalStepSaved?: () => void

  topicsQuery: UseQueryResult<GetSponsorTopicsResponse, DefaultError>
  sidekicksQuery: UseQueryResult<GetSidekicksResponse, DefaultError>
  sidekickPoolQuery: UseQueryResult<GetSidekicksResponse | null, DefaultError>
  contentModulesQuery: UseQueryResult<GetContentModulesResponse, DefaultError>

  cmQuery: UseQueryResult<GetContentModuleResponse, DefaultError>
  cmContentQuery: UseQueryResult<GetContentResponse, DefaultError>
  cmFAQsQuery: UseQueryResult<GetFAQsResponse, DefaultError>
  cmResourcesQuery: UseQueryResult<Array<GetResourcesResponse[0] & { valid: boolean }>, DefaultError>
  cmCreateMutation: UseMutationResult<CreateContentModuleResponse, DefaultError, CreateContentModuleParams>
  cmUpdateMutation: UseMutationResult<UpdateContentModuleResponse, DefaultError, UpdateContentModuleParams>

  stepsQuery: StepsWithValidationResult
  stepContentQuery: UseQueryResult<GetContentResponse, DefaultError>
  stepFAQsQuery: UseQueryResult<GetFAQsResponse, DefaultError>
  stepCreateMutation: UseMutationResult<CreateStepResponse, DefaultError, CreateStepParams>
  stepDeleteMutation: UseMutationResult<unknown, DefaultError, DeleteStepParams>
  stepUpdateMutation: UseMutationResult<unknown, DefaultError, UpdateStepParams>

  faqsCreateMutation: UseMutationResult<unknown, DefaultError, CreateFAQsParams>
  faqsUpdateMutation: UseMutationResult<unknown, DefaultError, UpdateFAQsParams>
  faqsDeleteMutation: UseMutationResult<unknown, DefaultError, DeleteFAQsParams>

  resourceCreateMutation: UseMutationResult<CreateResourceResponse, DefaultError, CreateResourceParams>
  resourceUpdateMutation: UseMutationResult<UpdateResourceResponse, DefaultError, UpdateResourceParams>
  resourceDeleteMutation: UseMutationResult<unknown, DefaultError, DeleteResourceParams>

  contentUpdateMutation: UseMutationResult<UpdateContentTextResponse, DefaultError, UpdateContentTextParams>

  generateMetadataMutation: UseMutationResult<GenerateMetadataResponse, DefaultError, GenerateMetadataParams>
  generateObjectivesMutation: UseMutationResult<GenerateObjectivesResponse, DefaultError, GenerateObjectivesParams>
  generateDemographicMutation: UseMutationResult<GenerateDemographicResponse, DefaultError, GenerateDemographicParams>
  generateOutlineMutation: UseMutationResult<GenerateOutlineResponse, DefaultError, GenerateOutlineParams>
}

// Only allow safe modal props to be customized to prevent overriding security settings
type CustomizableModalProps = Pick<ModalProps, 'size' | 'styles' | 'classNames'>

type ModalContent = {
  ModalProps?: Partial<CustomizableModalProps>
  Component: ComponentType<any>
  ComponentProps?: object
  title?: string
  subtitle?: string
}

export const ContentModuleFormScreen = observer(function ContentModuleFormScreen() {
  const params = useParams<RouteParams['contentModuleFormEdit'] | RouteParams['contentModuleFormNew']>()

  const { api, authenticationStore } = useGlobalState()
  const { user } = authenticationStore

  const { data: moduleData } = useQuery({
    queryKey: ['module', params.id],
    queryFn: () => api.getContentModule({ task_id: params.id! }),
    enabled: !!params.id,
  })
  const { form: moduleForm } = useContentModuleForm({
    debug: true,
    initialData: moduleData,
  })
  const { form: resourceForm } = useResourceForm({ debug: true })
  const { form: stepForm } = useStepForm({ debug: true })
  const metadataForm = useMetadataForm({ debug: true })

  // Validation error clearing mechanism
  const [clearValidationTrigger, setClearValidationTrigger] = useState(0)
  const clearValidationErrors = useCallback(() => {
    setClearValidationTrigger(prev => prev + 1)
  }, [])

  // Step saved trigger - signals when a step has been committed via "Save & Exit"
  const [stepSavedTrigger, setStepSavedTrigger] = useState(0)
  const signalStepSaved = useCallback(() => {
    setStepSavedTrigger(prev => prev + 1)
  }, [])

  // FAQ State Management
  const [cmApprovedFAQs, setCMApprovedFAQs] = useState<FAQItem[]>([])
  const [cmPendingFAQs, setCMPendingFAQs] = useState<FAQItem[]>([])
  const [cmShowValidationErrors, setCMShowValidationErrors] = useState(false)
  const [cmIsInitialized, setCMIsInitialized] = useState(false)

  const [stepApprovedFAQs, setStepApprovedFAQs] = useState<FAQItem[]>([])
  const [stepPendingFAQs, setStepPendingFAQs] = useState<FAQItem[]>([])
  const [stepShowValidationErrors, setStepShowValidationErrors] = useState(false)
  const [stepIsInitialized, setStepIsInitialized] = useState(false)

  const taskId = params.id
  const isNew = useMemo(() => !params.id, [params.id])
  const stepId = useMemo(() => stepForm.getValues().stepId ?? undefined, [stepForm])
  const resourceId = useMemo(() => resourceForm.getValues().resourceId ?? undefined, [resourceForm])

  const [modalOpen, modal] = useDisclosure(false)
  const [modalContent, setModalContent] = useState<ModalContent[]>([])
  const modalScrollPositions = useRef<{ [key: string]: number }>({})
  const modalScrollAreaViewport = useRef<HTMLDivElement>(null)
  const ModalComponent = useMemo(() => modalContent[0]?.Component, [modalContent]) as ComponentType<CMFormSection>
  const ModalScrollArea = useCallback(
    (props: ScrollAreaProps) => (
      <ScrollArea.Autosize {...props} viewportRef={modalScrollAreaViewport} type="always" scrollbarSize={16} />
    ),
    [],
  )

  const sectionRefs = {
    [FormSectionId.TopicSettings]: useRef<CollapsibleContentSectionRef>(undefined),
    [FormSectionId.AddData]: useRef<CollapsibleContentSectionRef>(undefined),
    [FormSectionId.CMSettings]: useRef<CollapsibleContentSectionRef>(undefined),
    [FormSectionId.CustomizeDelivery]: useRef<CollapsibleContentSectionRef>(undefined),
    [FormSectionId.CMOptionalSettings]: useRef<CollapsibleContentSectionRef>(undefined),
    [FormSectionId.ContentSteps]: useRef<CollapsibleContentSectionRef>(undefined),
    [FormSectionId.Actions]: useRef<CollapsibleContentSectionRef>(undefined),
  }

  // Ref for OptionalSettingsSection content to access FAQ saving
  const optionalSettingsSectionRef = useRef<OptionalSettingsSectionRef>(null)

  const topicsQuery = useQuery({
    queryKey: ['sponsor', 'topics'],
    queryFn: api.getSponsorTopics,
    initialData: [],
  })

  const sidekicksQuery = useQuery({
    queryKey: ['sponsor', 'sidekicks'],
    queryFn: api.getSidekicks,
    initialData: {
      mskTypes: [],
      eskTypes: [],
    },
  })

  const sidekickPoolQuery = useQuery({
    queryKey: ['sponsor', 'sidekick-pool'],
    queryFn: api.getSidekickPool,
    initialData: null,
  })

  const contentModulesQuery = useQuery({
    queryKey: ['cm', 'list'],
    queryFn: () => api.getContentModules(),
    initialData: [],
  })

  // Compute first available ESK for new modules
  const firstAvailableEskId = useMemo(() => {
    const sponsorEsks = sidekicksQuery.data?.eskTypes ?? []
    const poolEsks = sidekickPoolQuery.data?.eskTypes ?? []
    const sponsorEskNames = new Set(sponsorEsks.map(esk => esk.name))
    const poolEskOptions = poolEsks.filter(esk => !sponsorEskNames.has(esk.name))
    const allEsks = [...sponsorEsks, ...poolEskOptions]

    const assignedEskIds = new Set(contentModulesQuery.data.filter(cm => cm.selectEsk).map(cm => cm.selectEsk))

    const availableEsk = allEsks.find(e => !assignedEskIds.has(e.type))
    return availableEsk?.type ?? null
  }, [sidekicksQuery.data, sidekickPoolQuery.data, contentModulesQuery.data])

  const cmQuery = useQuery({
    queryKey: ['cm', taskId],
    queryFn: () => api.getContentModule({ task_id: taskId! }),
    enabled: !isNew,
    gcTime: 0,
    retry: false,
  })

  const isSponsorMskCM = cmQuery.data ? isSponsorMskCm(cmQuery.data) : false

  const additionalEndpointsEnabled = useMemo(() => {
    const isSponsor = (user?.isSponsor || user?.isSponsorAdmin) ?? false
    // If this is a SponsorMSK CM and the user is a Sponsor, they get to edit it so we need to enable the additional endpoints
    const isSpecialCase = isSponsorMskCM && isSponsor
    const enabled = !isNew && cmQuery.isSuccess && (cmQuery.data.editable !== false || isSpecialCase)

    return enabled
  }, [isNew, cmQuery.data, cmQuery.isSuccess])

  const cmResourcesQuery = useQuery({
    queryKey: ['cm', 'resources', taskId],
    queryFn: () => api.getResources({ task_id: taskId! }),
    select: data => data.map(d => ({ ...d, valid: !!d.title && !!d.description && !!d.data })),
    enabled: additionalEndpointsEnabled,
    gcTime: 0,
  })

  const cmContentQuery = useQuery({
    queryKey: ['cm', 'content', taskId],
    queryFn: () => api.getContent({ task_id: taskId! }),
    enabled: additionalEndpointsEnabled,
  })

  const cmFAQsQuery = useQuery({
    queryKey: ['cm', 'faqs', taskId],
    queryFn: () => api.getFAQs({ task_id: taskId! }),
    enabled: additionalEndpointsEnabled,
  })

  const baseStepsQuery = useQuery({
    queryKey: ['steps', taskId],
    queryFn: () => api.getSteps({ task_id: taskId! }),
    enabled: !!taskId && !isNew,
    gcTime: 0,
  })

  const stepsQuery = useStepsWithValidation(baseStepsQuery)

  const stepContentQuery = useQuery({
    queryKey: ['step', 'content', stepId],
    queryFn: () => api.getContent({ step_id: stepId! }),
    enabled: !!stepId,
  })

  const stepFAQsQuery = useQuery({
    queryKey: ['step', 'faqs', stepId],
    queryFn: () => api.getFAQs({ step_id: stepId! }),
    enabled: !!stepId,
  })

  const cmCreateMutation = useMutation({
    mutationFn: api.createContentModule,
    onSuccess: data => {
      api.queryClient.invalidateQueries({ queryKey: ['cm', 'list'] })

      navigate(getRoutePath('contentModuleFormEdit', { id: data.taskId }), { replace: true })

      notifications.show({
        title: 'Module Created',
        message: 'The module was successfully created.',
        color: 'green',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })

      sectionRefs.requiredSettings.current?.open()
      sectionRefs.optionalSettings.current?.open()
      sectionRefs.contentSteps.current?.open()
    },
    onError: () => {
      notifications.show({
        title: 'Something went wrong.',
        message: 'There was an issue creating the module. Please try again or contact support.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    },
  })

  const cmUpdateMutation = useMutation({
    mutationFn: api.updateContentModule,
    onSuccess: () => {
      api.queryClient.invalidateQueries({ queryKey: ['cm', 'list'] })
    },
    onError: () => undefined,
  })

  const stepUpdateMutation = useMutation({
    mutationFn: api.updateStep,
    onSuccess: () => {
      stepsQuery.refetch()
      clearValidationErrors()
    },
    onError: () => undefined,
  })

  const contentUpdateMutation = useMutation({
    mutationFn: api.updateContentText,
    onSuccess: () => {
      cmContentQuery.refetch()
      if (stepId) stepContentQuery.refetch()
      clearValidationErrors()
    },
    onError: () => undefined,
  })

  const faqsCreateMutation = useMutation({
    mutationFn: api.createFAQs,
    onSuccess: () => {
      cmFAQsQuery.refetch()
      if (stepId) stepFAQsQuery.refetch()
    },
    onError: () => undefined,
  })

  const faqsUpdateMutation = useMutation({
    mutationFn: api.updateFAQs,
    onSuccess: () => {
      if (!stepId) cmFAQsQuery.refetch()
      if (stepId) stepFAQsQuery.refetch()
    },
    onError: () => undefined,
  })

  const faqsDeleteMutation = useMutation({
    mutationFn: api.deleteFAQs,
    onSuccess: () => {
      cmFAQsQuery.refetch()
      if (stepId) stepFAQsQuery.refetch()
    },
    onError: () => undefined,
  })

  const resourceCreateMutation = useMutation({
    mutationFn: api.createResource,
    onSuccess: async (data, variables) => {
      // Get the data field we sent to identify the resource (since API may return null resourceId)
      const sentData = variables.data

      const resource = await (async function () {
        if (!stepId) {
          const resources = (await cmResourcesQuery.refetch()).data ?? []
          // Try to find by resourceId first, then fall back to matching by data
          const byId = resources.find(r => r.resourceId === data.resourceId)
          const byData = resources.find(r => r.data === sentData)
          return byId || byData
        } else {
          // Invalidate both resource query keys (useStepModalState uses 'all', useStepsWithValidation doesn't)
          await api.queryClient.invalidateQueries({ queryKey: ['step', 'resources', 'all', stepId] })
          await api.queryClient.invalidateQueries({ queryKey: ['step', 'resources', stepId] })
          // Fetch resources directly instead of relying on stepsQuery
          const resources = await api.request<GetResourcesResponse>('/cm/get_resources', 'get', {
            step_id: stepId,
          })
          // Try to find by resourceId first, then fall back to matching by data
          const byId = resources.find(r => r.resourceId === data.resourceId)
          const byData = resources.find(r => r.data === sentData)
          return byId || byData
        }
      })()

      if (resource) {
        resourceForm.setValues(castResourceToZodForm(resource))
      }
    },
    onError: () => undefined,
  })

  const resourceUpdateMutation = useMutation({
    mutationFn: api.updateResource,
    onSuccess: async (_data, vars) => {
      const resource = await (async function () {
        if (!stepId) {
          const resources = (await cmResourcesQuery.refetch()).data ?? []
          return resources.find(r => r.resourceId === vars.resource_id)
        } else {
          // Invalidate both resource query keys (useStepModalState uses 'all', useStepsWithValidation doesn't)
          await api.queryClient.invalidateQueries({ queryKey: ['step', 'resources', 'all', stepId] })
          await api.queryClient.invalidateQueries({ queryKey: ['step', 'resources', stepId] })
          const refetchedSteps = await stepsQuery.refetch()
          const updatedStep = refetchedSteps.data?.find(step => step.stepId === stepId)
          return updatedStep?.resources?.find(r => r.resourceId === vars.resource_id)
        }
      })()

      if (resource) {
        resourceForm.setValues(castResourceToZodForm(resource))
        resourceForm.resetDirty()
      }
    },
    onError: () => undefined,
  })

  const resourceDeleteMutation = useMutation({
    mutationFn: api.deleteResource,
    onSuccess: () => {
      cmResourcesQuery.refetch()

      if (stepId) {
        // Invalidate both resource query keys (useStepModalState uses 'all', useStepsWithValidation doesn't)
        api.queryClient.invalidateQueries({ queryKey: ['step', 'resources', 'all', stepId] })
        api.queryClient.invalidateQueries({ queryKey: ['step', 'resources', stepId] })
      }

      notifications.show({
        title: 'Resource Deleted',
        message: 'The Resource was successfully deleted.',
        color: 'green',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    },
    onError: () => undefined,
  })

  const stepCreateMutation = useMutation({
    mutationFn: api.createStep,
    onSuccess: async data => {
      notifications.show({
        title: 'File Created',
        message: 'The file was successfully created.',
        color: 'green',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })

      const steps = await stepsQuery.refetch()

      const step = steps.data?.find(step => step.stepId === data.stepId)

      if (step) {
        stepForm.setValues(castStepToZodForm(step))
      }

      clearValidationErrors()
    },
    onError: () => {
      notifications.show({
        title: 'Something went wrong.',
        message: 'There was an issue creating the file. Please try again or contact support.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    },
  })

  const stepDeleteMutation = useMutation({
    mutationFn: api.deleteStep,
    onSuccess: (_data, { showToast = true }) => {
      api.queryClient.invalidateQueries({ queryKey: ['steps', taskId] })

      if (showToast) {
        notifications.show({
          title: 'File Deleted',
          message: 'The file was successfully deleted.',
          color: 'green',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
        })
      }
    },
    onError: () => undefined,
  })

  const generateMetadataMutation = useMutation({
    mutationFn: api.generateMetadata,
    onMutate: vars => {
      if ('task_id' in vars) {
        metadataForm.setFieldValue('module', undefined)
      } else if ('step_id' in vars) {
        metadataForm.setFieldValue('step', undefined)
      } else if ('resource_id' in vars) {
        metadataForm.setFieldValue('resource', undefined)
      }
    },
    onSuccess: (data, vars) => {
      if ('task_id' in vars) {
        metadataForm.setFieldValue('module', data.generated[0])
      } else if ('step_id' in vars) {
        metadataForm.setFieldValue('step', data.generated[0])
      } else if ('resource_id' in vars) {
        metadataForm.setFieldValue('resource', data.generated[0])
      }
    },
    onError: () => undefined,
  })

  const generateDemographicMutation = useMutation({
    mutationFn: api.generateDemographic,
    onMutate: vars => {
      if ('task_id' in vars) {
        metadataForm.setFieldValue('demographic', undefined)
      }
    },
    onSuccess: (data, vars) => {
      if ('task_id' in vars) {
        metadataForm.setFieldValue('demographic', data.demographic)
      }
    },
    onError: () => undefined,
  })

  const generateObjectivesMutation = useMutation({
    mutationFn: api.generateObjectives,
    onMutate: vars => {
      if ('task_id' in vars) {
        metadataForm.setFieldValue('sectionObjectives', undefined)
      }
    },
    onSuccess: (data, vars) => {
      if ('task_id' in vars) {
        metadataForm.setFieldValue('sectionObjectives', data.objectives)
      }
    },
    onError: () => undefined,
  })

  const generateOutlineMutation = useMutation({
    mutationFn: api.generateOutline,
    onMutate: vars => {
      if ('task_id' in vars) {
        metadataForm.setFieldValue('outline', undefined)
      }
    },
    onSuccess: (data, vars) => {
      if ('task_id' in vars) {
        metadataForm.setFieldValue('outline', data.outline)
      }
    },
    onError: () => undefined,
  })

  useEffect(() => {
    if (!cmQuery.isSuccess) return

    const formData = castContentModuleToZodForm(cmQuery.data)

    // Default to first available ESK for new modules without an ESK assigned
    if (!formData.selectEsk && firstAvailableEskId) {
      formData.selectEsk = firstAvailableEskId
    }

    moduleForm.initialize(formData)
    moduleForm.resetDirty()
  }, [cmQuery.data, cmQuery.isFetched, cmQuery.isSuccess, firstAvailableEskId])

  const openModal = useCallback(
    (content?: ModalContent) => {
      if (content) {
        setModalContent(prevState => {
          const currentModal = prevState[0]

          if (currentModal) {
            modalScrollPositions.current[dasherize(currentModal.title ?? '')] =
              modalScrollAreaViewport.current?.scrollTop ?? 0
          }

          return [content, ...prevState]
        })

        setTimeout(() => modalScrollAreaViewport.current?.scrollTo({ top: 0, behavior: 'instant' }), 50)

        modal.open()
      }
    },
    [stepApprovedFAQs.length],
  )

  const closeModal: CMFormSection['closeModal'] = useCallback(
    afterCloseFn => {
      const shouldCompletelyClose = modalContent.length === 1

      if (shouldCompletelyClose) {
        modal.close()
        modalScrollPositions.current = {}
      }
      setTimeout(
        () => {
          let nextModal: ModalContent | undefined

          setModalContent(prevState => {
            const [_current, ...rest] = prevState
            nextModal = rest[0]
            return rest
          })

          afterCloseFn?.()

          const nextModalPosY = modalScrollPositions.current[dasherize(nextModal?.title ?? '')] ?? 0

          setTimeout(() => {
            modalScrollAreaViewport.current?.scrollTo({ top: nextModalPosY, behavior: 'instant' })
          }, 50)
        },
        shouldCompletelyClose ? 300 : 0,
      )
    },
    [modalContent, stepApprovedFAQs.length],
  )

  // Store field refs from sections
  const fieldRefsStore = useRef<Record<string, React.RefObject<HTMLElement>>>({})

  const scrollToSection = useCallback((section: `${FormSectionId}`, delay?: number) => {
    setTimeout(() => sectionRefs[section].current?.scrollIntoView(), delay ?? 0)
  }, [])

  const scrollToField = useCallback((fieldName: string, delay?: number) => {
    setTimeout(() => {
      const fieldRef = fieldRefsStore.current[fieldName]
      if (fieldRef?.current) {
        fieldRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
        fieldRef.current.focus()
      }
    }, delay ?? 0)
  }, [])

  const setFieldRefs = useCallback((refs: Record<string, React.RefObject<HTMLElement>>) => {
    fieldRefsStore.current = { ...fieldRefsStore.current, ...refs }
  }, [])

  const commonData = { taskId, isNew, stepId, resourceId, clearValidationTrigger, stepSavedTrigger }
  const forms = { moduleForm, resourceForm, stepForm, metadataForm }
  const controls = useMemo(
    () => ({
      openModal,
      closeModal,
      scrollToSection,
      scrollToField,
      setFieldRefs,
      clearValidationErrors,
      signalStepSaved,
    }),
    [openModal, closeModal, scrollToSection, scrollToField, setFieldRefs, clearValidationErrors, signalStepSaved],
  )

  // FAQ state and actions
  const faqState = useMemo(
    () => ({
      cmFAQState: {
        approved: cmApprovedFAQs,
        pending: cmPendingFAQs,
        showValidationErrors: cmShowValidationErrors,
        isInitialized: cmIsInitialized,
      },
      stepFAQState: {
        approved: stepApprovedFAQs,
        pending: stepPendingFAQs,
        showValidationErrors: stepShowValidationErrors,
        isInitialized: stepIsInitialized,
      },
      faqActions: {
        setCMApprovedFAQs,
        setCMPendingFAQs,
        setCMShowValidationErrors,
        setCMIsInitialized,
        setStepApprovedFAQs,
        setStepPendingFAQs,
        setStepShowValidationErrors,
        setStepIsInitialized,
        resetStepFAQState: () => {
          setStepApprovedFAQs([])
          setStepPendingFAQs([])
          setStepShowValidationErrors(false)
          setStepIsInitialized(false)
        },
      },
    }),
    [
      cmApprovedFAQs,
      cmPendingFAQs,
      cmShowValidationErrors,
      cmIsInitialized,
      stepApprovedFAQs,
      stepPendingFAQs,
      stepShowValidationErrors,
      stepIsInitialized,
    ],
  )
  const operations = useMemo(
    () => ({
      cmQuery,
      cmContentQuery,
      cmFAQsQuery,
      cmResourcesQuery,
      sidekicksQuery,
      sidekickPoolQuery,
      contentModulesQuery,
      stepsQuery,
      cmCreateMutation,
      cmUpdateMutation,
      contentUpdateMutation,
      faqsCreateMutation,
      faqsUpdateMutation,
      faqsDeleteMutation,
      resourceCreateMutation,
      resourceUpdateMutation,
      resourceDeleteMutation,
      stepCreateMutation,
      stepDeleteMutation,
      stepFAQsQuery,
      stepUpdateMutation,
      stepContentQuery,
      generateMetadataMutation,
      generateObjectivesMutation,
      generateDemographicMutation,
      generateOutlineMutation,
      topicsQuery,
      // FAQ saving function for ActionsSection
      saveCMFAQs: async () => {
        await optionalSettingsSectionRef.current?.saveFAQs()
      },
    }),
    [
      cmQuery,
      cmContentQuery,
      cmFAQsQuery,
      sidekicksQuery,
      sidekickPoolQuery,
      contentModulesQuery,
      stepsQuery,
      cmResourcesQuery,
      cmCreateMutation,
      cmUpdateMutation,
      contentUpdateMutation,
      faqsCreateMutation,
      faqsUpdateMutation,
      faqsDeleteMutation,
      resourceCreateMutation,
      resourceUpdateMutation,
      resourceDeleteMutation,
      stepCreateMutation,
      stepDeleteMutation,
      stepFAQsQuery,
      stepUpdateMutation,
      stepContentQuery,
      generateMetadataMutation,
      generateObjectivesMutation,
      generateDemographicMutation,
      generateOutlineMutation,
      topicsQuery,
    ],
  )

  const sections = useMemo(
    () =>
      [
        {
          id: FormSectionId.TopicSettings,
          sectionRef: sectionRefs.topicSettings,
          collapsible: false,
          defaultCollapsed: false,
          title: 'Select Sidekick',
          subtitle: undefined,
          ContentElement: (
            <TopicSettingsSection {...commonData} {...controls} {...forms} {...operations} {...faqState} />
          ),
          valid: validateFieldsList(moduleForm, ['selectEsk']).isValid,
        },
        {
          id: FormSectionId.AddData,
          sectionRef: sectionRefs.addData,
          collapsible: !isNew,
          defaultCollapsed: false,
          title: 'Add Primary Content',
          subtitle:
            'Upload the primary content file for this module or choose to enter your content manually. You will have the option to add supplemental files later.',
          ContentElement: <AddDataSection {...commonData} {...controls} {...forms} {...operations} {...faqState} />,
          valid: !!cmContentQuery.data,
          hidden: true,
        },
        {
          id: FormSectionId.ContentSteps,
          sectionRef: sectionRefs.contentSteps,
          collapsible: !isNew,
          defaultCollapsed: false,
          title: 'Add Content',
          subtitle: undefined,
          ContentElement: <StepsSection {...commonData} {...controls} {...forms} {...operations} {...faqState} />,
          valid:
            validateFieldsList(moduleForm, ['presentationMode']).isValid &&
            !!stepsQuery.data?.length &&
            !!stepsQuery.data?.every(step => step.valid),
        },
        {
          id: FormSectionId.CMSettings,
          sectionRef: sectionRefs.requiredSettings,
          collapsible: !isNew,
          defaultCollapsed: isNew,
          title: 'Describe Module',
          subtitle: undefined,
          ContentElement: (
            <RequiredSettingsSection {...commonData} {...controls} {...forms} {...operations} {...faqState} />
          ),
          valid: validateFieldsList(moduleForm, ['name', 'shortDescription', 'demographic', 'introduction']).isValid,
        },
        {
          id: FormSectionId.CustomizeDelivery,
          sectionRef: sectionRefs.customizeDelivery,
          collapsible: !isNew,
          defaultCollapsed: isNew,
          title: 'Customize Delivery',
          subtitle: 'Configure how content is delivered to users.',
          ContentElement: (
            <CustomizeDeliverySection {...commonData} {...controls} {...forms} {...operations} {...faqState} />
          ),
          valid: validateFieldsList(moduleForm, ['outline', 'deliveryStyle']).isValid,
        },
        {
          id: FormSectionId.CMOptionalSettings,
          sectionRef: sectionRefs.optionalSettings,
          hidden: true, // stepsQuery.data?.length === 1 && !cmFAQsQuery.data?.length && !cmResourcesQuery.data?.length,
          collapsible: !isNew,
          defaultCollapsed: isNew,
          title: 'Add Module FAQs and Supplemental Information',
          subtitle: 'Submit optional information for the module.',
          ContentElement: (
            <OptionalSettingsSection
              {...commonData}
              {...controls}
              {...forms}
              {...operations}
              {...faqState}
              ref={optionalSettingsSectionRef}
            />
          ),
        },
        {
          id: FormSectionId.Actions,
          sectionRef: sectionRefs.actions,
          hidden: isNew,
          collapsible: false,
          ContentElement: (
            <ActionsSection
              {...commonData}
              {...controls}
              {...forms}
              {...operations}
              {...faqState}
              isSponsorMskCM={!!isSponsorMskCM}
              isVisibleToMsk={!!cmQuery.data?.visibleToMsk}
            />
          ),
        },
      ] satisfies SectionedFormProps['sections'],
    [isNew, moduleForm, stepsQuery.data, cmContentQuery.data, isSponsorMskCM, faqState],
  )

  if (
    !isNew &&
    (cmQuery.isError ||
      (cmQuery.isSuccess && cmQuery.data.editable === false && !cmQuery.data.visibleToMsk && !user?.isSponsorAdmin))
  ) {
    return <NotFoundScreen />
  }
  return (
    <Screen
      title={isNew ? 'New Module' : ['Edit Module', cmQuery.data?.title].filter(Boolean).join(': ')}
      icon={IconHexagons}>
      <SectionedForm sections={sections} />

      <Modal
        opened={modalOpen}
        centered
        trapFocus
        radius="md"
        padding="lg"
        closeOnEscape={false}
        closeOnClickOutside={false}
        withCloseButton={false}
        size={modalContent[0]?.ModalProps?.size ?? theme.rem(1200)}
        onClose={closeModal}
        transitionProps={{ duration: 300 }}
        scrollAreaComponent={ModalScrollArea}
        styles={modalContent[0]?.ModalProps?.styles}
        classNames={{
          ...modalContent[0]?.ModalProps?.classNames,
          body: commonClasses.stickyModalFooterBody,
        }}
        title={
          modalContent[0]?.title && (
            <Stack gap={0}>
              <Text size="xl" fw={600}>
                {modalContent[0]?.title}
              </Text>

              {!!modalContent[0]?.subtitle && (
                <Text size="md" c="dimmed">
                  {modalContent[0].subtitle}
                </Text>
              )}
            </Stack>
          )
        }>
        {!!ModalComponent && (
          <ModalComponent
            {...commonData}
            {...controls}
            {...forms}
            {...operations}
            {...faqState}
            {...modalContent[0]?.ComponentProps}
          />
        )}
      </Modal>
    </Screen>
  )
})
